import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import useDataSelect from '../hooks/useDataSelect';
import axiosInstance from '../utils/axiosInstance';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { formatDate, formatDateTime, formatCurrency } from '../utils/format';  // 日付フォーマット関数のインポート
import DeliveryNotePrintButton from '../components/DeliveryNotePrintButton';
import TableView from '../components/TableView';
import { Button, Typography, Paper, Box, Stack, Divider, Alert, Grid, } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
const OrderDetailPage = () => {
  const { showSnackbar } = useSnackbar();
  const { id } = useParams();
  const { dataset: orderData, setDataset: setOrderData, loading: apiLoading, error: apiError, refetch } = useDataSelect(`/order/${id}`);
  const navigate = useNavigate();
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  /* 再注文 */
  const reorder = async (orderId, orderItemIds = []) => {
    if (reorderLoading) return;
    try {
      setReorderLoading(true);
      const res = await axiosInstance.post('/cart/reorder', {
        order_id: orderId,
        order_item_ids: orderItemIds,
      });
      showSnackbar(res?.data?.message || 'カートに追加しました', 'success');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || 'カートに追加できませんでした', 'error');

    } finally {
      setReorderLoading(false);
    }
  };

  // エラーが発生した場合
  if (apiError) return <GlobalError errorMessage={apiError} />;

  // 注文詳細が存在しない場合
  if (!orderData) {
    return <Typography>データが見つかりません</Typography>;
  }
  // カラム定義
  const columns = [
    {
      label: '',
      accessor: 'image_url',
      render: (item) => {
        return (
          <img
            alt={item.product_name || '商品画像'}
            src={
              item.image_url
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url.replace(/\\/g, '/')}`
                : `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`
            }
            className="product-image"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NO_IMAGE}`;
            }}
          />
        );
      },
      width: '80px',
    },
    {
      label: '商品',
      accessor: 'product_name',
      render: (item) => (
        <>
          <Typography>
            {item.sku}<br />
            {item.product_name}
            {item?.option_combination && Object.entries(item.option_combination).map(([type, value]) => (
              <> {value}</>
            ))}
          </Typography>
          <Typography>{formatCurrency(item.selling_price)}
            <Typography variant='caption'>（税込{formatCurrency(item.selling_price_including_tax)}）</Typography></Typography>
        </>
      ),
      minWidth: '250px',
    },
    {
      label: '数量',
      accessor: 'quantity',
      render: (item) => (
        <>{item.quantity}個</>
      ),
      width: '100px',
    },
    {
      label: '金額',
      accessor: 'subtotal',
      render: (item) => (
        <>
          <Typography>{formatCurrency(item.subtotal ?? 0)}</Typography>
          <Typography variant='caption'>税込{formatCurrency(item.subtotal_including_tax)}</Typography>
        </>
      ),
      minWidth: '200px',
    },
    {
      label: '',
      accessor: 'edit',
      render: (item) => (
        <Button
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => reorder(orderData.order_id, [item.order_item_id])}
        >
          再発注
        </Button>
      ),
      width: '80px',
    },
  ];
  const InfoItem = ({ label, value }) => (
    <Box>
      <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ overflowWrap: 'break-word', }}> {value || '-'}</Typography>
    </Box>
  );
  const handleCancelStatus = async () => {
    if (cancelLoading) return;
    const isConfirmed = window.confirm('本当にキャンセルしますか？\nこの操作は取り消せません。');
    if (!isConfirmed) return;
    try {
      setCancelLoading(true);
      await axiosInstance.post('/order/update-status', { order_id: id, });
      showSnackbar('注文をキャンセルしました。', 'success');
      setTimeout(() => { navigate('/orders'); }, 800);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') { console.error(err); }
      alert('キャンセルに失敗しました。\nお手数ですが直接を連絡ください。');
    } finally {
      setCancelLoading(false);
    }
  };

  // 注文詳細とアイテムの表示
  return (
    <>
      <Typography variant='h1' mt={6}>注文確認</Typography>
      <Paper variant='section' sx={{ pb: 10 }}>
        {orderData.order_status_id === '7' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            完了した注文です
          </Alert>
        )}
        {['8', '9'].includes(orderData.order_status_id) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            取り消し・キャンセルした注文です
          </Alert>
        )}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label="注文日時"
                value={formatDateTime(orderData.order_date)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem
                label="注文番号"
                value={orderData.order_number}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label="会社名"
                value={orderData.client_name}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem
                label="事業所名"
                value={orderData.branch_name}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem
                label="電話番号"
                value={orderData.client_phone}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem
                label="担当者名"
                value={orderData.client_contact_name}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem
                label="担当者TEL"
                value={orderData.client_contact_phone}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <InfoItem
                label="納品場所"
                value={`〒 ${orderData.postal_code} ${orderData.state}${orderData.address_line}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label="宛名（受取人名）"
                value={orderData.recipient_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                label="配送希望日"
                value={formatDate(orderData.delivery_date)}
              />
            </Grid>
            {/*<Grid item xs={12} sm={6}>
              <InfoItem
                label="支払い条件"
                value={orderData.payment_terms}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem
                label="支払方法"
                value={orderData.payment_method_name}
              />
            </Grid>*/}
          </Grid>
        </Box>
        <Divider sx={{ mb: 4 }} />
        <TableView dataset={orderData.items} columns={columns} />
        {/* 合計金額 */}
        <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight="bold">合計金額（税抜）</Typography>
              <Typography fontWeight="bold">{formatCurrency(orderData.total_amount)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography fontWeight="bold">合計金額（税込）</Typography>
              <Typography fontSize={22} fontWeight="bold">{formatCurrency(orderData.total_amount_including_tax)}</Typography>
            </Stack>
            <Divider />
          </Stack>
        </Box>
      </Paper>
      <div className='button-row'>
        {['1', '2'].includes(orderData.order_status_id) && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancelStatus}
            disabled={cancelLoading}
          >
            注文のキャンセル
          </Button>
        )}

        {(orderData.order_status_id === '7') && (
          <DeliveryNotePrintButton
            orderData={orderData}
            loading={apiLoading}
            error={apiError}
          />
        )}
        <Button
          variant="contained"
          color="primary"
          endIcon={<ChevronRightIcon />}
          onClick={() => reorder(orderData.order_id)}
          disabled={reorderLoading}
        >
          再発注
        </Button>
      </div>
    </>
  );
};

export default OrderDetailPage;
